import { toast } from "@/lib/toast";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "@groot/ui/primitives";

/**
 * Configuration for detail page hook
 */
interface UseDetailPageConfig<T, TId = string> {
  /** Function to fetch the data */
  fetchFn: (id: TId) => Promise<T>;
  /** Function to delete the data (optional) */
  deleteFn?: (id: TId) => Promise<void>;
  /** ID of the resource */
  id: TId | undefined;
  /** Path to navigate to after deletion */
  redirectPath?: string;
  /** Custom delete confirmation message */
  deleteConfirmMessage?: string;
  /** Resource name for toast messages (e.g., "SMS", "Email", "Job") */
  resourceName?: string;
}

/**
 * Return type for useDetailPage hook
 */
interface UseDetailPageReturn<T> {
  /** The loaded data */
  data: T | null;
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Function to reload the data */
  reload: () => Promise<void>;
  /** Function to handle deletion */
  handleDelete: () => Promise<void>;
}

/**
 * Reusable hook for detail page pattern
 * Handles loading, error states, and common operations for detail pages
 *
 * @example
 * const { id } = useParams<{ id: string }>();
 * const { data, loading, error, reload, handleDelete } = useDetailPage({
 *   fetchFn: (id) => apiClient.getTransactionalSmsById(Number(id)),
 *   deleteFn: (id) => apiClient.deleteTransactionalSms(Number(id)),
 *   id,
 *   redirectPath: '/transactional-sms',
 *   resourceName: 'SMS',
 * });
 */
export function useDetailPage<T, TId = string>({
  fetchFn,
  deleteFn,
  id,
  redirectPath,
  deleteConfirmMessage,
  resourceName = "item",
}: UseDetailPageConfig<T, TId>): UseDetailPageReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const loadData = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await fetchFn(id);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to load ${resourceName}`;
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [id, fetchFn, resourceName, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = useCallback(async () => {
    if (!id || !deleteFn) return;

    const message = deleteConfirmMessage ?? `Are you sure you want to delete this ${resourceName}?`;
    if (!(await confirm({ title: "Confirm Action", description: message }))) {
      return;
    }

    try {
      await deleteFn(id);
      toast({
        title: "Success",
        description: `${resourceName.charAt(0).toUpperCase() + resourceName.slice(1)} deleted successfully`,
      });

      if (redirectPath) {
        navigate(redirectPath);
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : `Failed to delete ${resourceName}`,
      });
    }
  }, [id, deleteFn, deleteConfirmMessage, resourceName, confirm, toast, navigate, redirectPath]);

  return {
    data,
    loading,
    error,
    reload: loadData,
    handleDelete,
  };
}
